import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import uniqueId from 'lodash/uniqueId';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ProgressStep } from '../../store/progressStore/types/ProgressStep';
import useProgressStore from '../../store/progressStore/useProgressStore';
import { locator } from '../../testing/e2e-locator/locator';
import { CheckBoxIcon } from '../Icons/CheckBoxIcon';
import { CheckBoxOutlineBlankIcon } from '../Icons/CheckBoxOutlineBlankIcon';

type ApplicationProgressModalProps = {
  progressSteps: ProgressStep[];
  onClose: () => void;
};

export const ApplicationProgressModal = ({
  progressSteps,
  onClose,
}: ApplicationProgressModalProps): ReactNode => {
  const { isLastStep } = useProgressStore();
  const { t } = useTranslation();

  return (
    <Modal
      data-testid={locator.components.applicationProgressModal.root}
      open
      onClose={onClose}
    >
      <Box>
        <header
          data-testid={locator.components.applicationProgressModal.header.root}
        >
          <h1
            data-testid={locator.components.applicationProgressModal.header.h1}
          >
            {t('applicationProgressModal.applicationProgress')}
          </h1>
          <button
            aria-label="Close progress"
            data-testid={
              locator.components.applicationProgressModal.header.closeButton
            }
            type="button"
            onClick={onClose}
          >
            <CloseRoundedIcon />
          </button>
        </header>
        <div>
          <main
            data-testid={locator.components.applicationProgressModal.main.root}
          >
            <Stepper
              activeStep={progressSteps.findIndex(
                (step): boolean => !step.completed,
              )}
              data-testid={
                locator.components.applicationProgressModal.main.stepper
              }
              orientation="vertical"
            >
              {progressSteps.map(
                (step): ReactNode => (
                  <Step
                    completed={step.completed}
                    key={uniqueId('modal-step-')}
                    expanded
                  >
                    <StepLabel
                      icon={
                        step.completed ? (
                          <CheckBoxIcon />
                        ) : (
                          <CheckBoxOutlineBlankIcon />
                        )
                      }
                    >
                      {step.title}
                    </StepLabel>
                    <StepContent
                      className={isLastStep(step) ? 'lastProgressStep' : ''}
                    >
                      {step.description}
                    </StepContent>
                  </Step>
                ),
              )}
            </Stepper>
          </main>
          <footer
            data-testid={
              locator.components.applicationProgressModal.footer.root
            }
          >
            <Button
              color="primary"
              data-testid={
                locator.components.applicationProgressModal.footer.closeButton
              }
              endIcon={<></>}
              loading={false}
              loadingPosition="end"
              type="button"
              variant="contained"
              fullWidth
              onClick={onClose}
            >
              {t('applicationProgressModal.close')}
            </Button>
          </footer>
        </div>
      </Box>
    </Modal>
  );
};
